import { ChevronDownIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { MouseEvent } from 'react';

import { PlaylistEntry as PlaylistEntryType } from '../types';
import { ResourceTypeIcon } from '../../ui/components/ResourceTypeIcon';
import { Button } from '../../ui/components/Button';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { ResourceType, ScriptureMeta } from '../../resources/types';
import { Menu, MenuButton } from '@headlessui/react';
import { PlaylistThemeSelector } from './PlaylistThemeSelector';
import { PaletteIcon } from '../../ui/icons/PaletteIcon';

type Props = {
  active?: boolean,
  entry: PlaylistEntryType,
  index: number,
  onClick?: (evt: MouseEvent) => void,
  onDelete?: (evt: MouseEvent) => void,
  onEdit?: () => void,
  onSetTheme?: (themeID: string | undefined) => void,
}

export const PlaylistEntry = ({ active, entry, index, onClick, onDelete, onEdit, onSetTheme }: Props) => {

  const { isOver, setNodeRef: setDroppableRef } = useDroppable({
    id: `playlist-entry-${index}`,
    data: {
      type: 'playlistEntry',
      index
    }
  });

  const { attributes, listeners, setNodeRef: setDraggableRef } = useDraggable({
    id: `playlist-entry-${index}`,
    data: {
      index,
      type: 'playlistEntry',
      resource: {
        title: entry.title,
        type: entry.type
      }
    }
  });

  const setNodeRef = (element: HTMLElement | null) => {
    setDraggableRef(element);
    setDroppableRef(element);
  };

  const classes = classNames(
    'group flex items-center justify-between p-2 pr-6 border border-t-0 first:border-t',
    'hover:bg-blue-100 cursor-pointer select-none relative',
    {
      'bg-blue-200': active,
      'bg-white': !active,
      'border-t-4 first:border-t-4 border-t-jw-blue': isOver
    }
  );

  return (
    <div
      className={classes}
      onClick={onClick}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
    >
      <ResourceTypeIcon
        className='size-8 min-w-8 mr-2'
        resourceType={entry.type}
      />
      <span className='grow'>
        {entry.title}
      </span>
      {
        entry.themeID && (
          <PaletteIcon className='size-4 text-neutral-400 mx-1' />
        )
      }
      {
        entry.type === ResourceType.SCRIPTURE && (
          <span className='text-sm text-neutral-400 mx-1'>
            {(entry.resource?.meta as ScriptureMeta)?.version}
          </span>
        )
      }
      <div className='hidden group-hover:flex absolute right-2'>
        <Menu>
          <MenuButton
            as={Button}
            className='p-1'
            onClick={e => e.stopPropagation()}
          >
            <PaletteIcon className='size-4' />
            <ChevronDownIcon
              aria-hidden="true"
              className="h-3 w-3"
            />
          </MenuButton>
          <PlaylistThemeSelector
            onSelectTheme={onSetTheme}
            selectedThemeID={entry.themeID}
          />
        </Menu>
        {
          onEdit && entry.type !== ResourceType.SCRIPTURE && (
            <Button
              className='p-1'
              onClick={onEdit}
            >
              <PencilIcon className='size-4' />
            </Button>
          )
        }
        {
          onDelete && (
            <Button
              className='bg-red-600 hover:bg-red-400 text-white p-1'
              onClick={onDelete}
            >
              <TrashIcon className='size-4' />
            </Button>

          )
        }
      </div>
    </div>
  );
};
